/**
 * ASAB Models (Augmented Service Access Bridge) composable for the Model Gateway feature
 * Provides a convenient interface for components to interact with ASAB models
 * Handles routing configuration, rate limiting, and notification handling for CRUD operations
 */

import { computed } from 'vue';
import { useModelGatewayStore } from '@/stores/modelGateway';
import type {
  AsabModelResponse,
  CreateAsabModelRequest,
  UpdateAsabModelRequest,
  AsabModelQueryParams,
} from '../types';

/**
 * Composable for managing ASAB models (Augmented Service Access Bridge)
 * Exposes store state and actions with notification handling
 * Handles routing configuration with load balancing and fallback chains
 * Handles rate limit configuration (RPM/TPM)
 */
export function useAsabModels() {
  const store = useModelGatewayStore();

  /**
   * Fetch ASAB models with optional filtering
   * @param params - Optional query parameters for filtering
   */
  const fetchAsabModels = async (params?: AsabModelQueryParams): Promise<void> => {
    await store.fetchAsabModels(params);
  };

  /**
   * Create a new ASAB model with routing configuration
   * @param data - The ASAB model configuration including routing targets, fallback chain, and rate limits
   * @returns Promise that resolves to the created model if successful, null otherwise
   */
  const createAsabModel = async (
    data: CreateAsabModelRequest
  ): Promise<AsabModelResponse | null> => {
    return await store.createAsabModel(data);
  };

  /**
   * Update an existing ASAB model
   * @param slug - The unique identifier for the ASAB model
   * @param data - The updated ASAB model configuration
   * @returns Promise that resolves to the updated model if successful, null otherwise
   */
  const updateAsabModel = async (
    slug: string,
    data: UpdateAsabModelRequest
  ): Promise<AsabModelResponse | null> => {
    return await store.updateAsabModel(slug, data);
  };

  /**
   * Enable an ASAB model
   * @param slug - The unique identifier for the ASAB model
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const enableAsabModel = async (slug: string): Promise<boolean> => {
    return await store.enableAsabModel(slug);
  };

  /**
   * Disable an ASAB model
   * @param slug - The unique identifier for the ASAB model
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const disableAsabModel = async (slug: string): Promise<boolean> => {
    return await store.disableAsabModel(slug);
  };

  /**
   * Delete an ASAB model
   * @param slug - The unique identifier for the ASAB model
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const deleteAsabModel = async (slug: string): Promise<boolean> => {
    return await store.deleteAsabModel(slug);
  };

  /**
   * Get an ASAB model by slug
   * @param slug - The unique identifier for the ASAB model
   * @returns The ASAB model if found, undefined otherwise
   */
  const getAsabModelBySlug = (slug: string): AsabModelResponse | undefined => {
    return store.getAsabModelBySlug(slug);
  };

  return {
    // State - all ASAB models from store
    asabModels: computed(() => store.asabModels),

    // Loading and error state
    isLoading: computed(() => store.isLoadingAsabModels),
    error: computed(() => store.asabModelsError),

    // Actions
    fetchAsabModels,
    createAsabModel,
    updateAsabModel,
    enableAsabModel,
    disableAsabModel,
    deleteAsabModel,
    getAsabModelBySlug,
  };
}
